import {useMutation} from '@apollo/client'
import {
  Button,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Tab,
  Tabs,
  Toolbar,
} from '@mui/material'
import {makeStyles} from '@mui/styles'
import gql from 'graphql-tag'
import {FC, useContext, useMemo, useState} from 'react'
import {useGetIdentity, useLogout, useTranslate} from 'react-admin'

import {SandboxContext} from '../../contexts/SandboxContext'
import useIsDesktop from '../../hooks/useIsDesktop'
import useSessionMerchantUser from '../../hooks/useSessionMerchantUser'
import {MerchantUserProfileEdit} from '../../resources/merchantUsersResource'
import {MerchantUsers} from '../../types/graphqlSchema'
import DialogCloseButton from '../DialogCloseButton'
import ExtendedDialog from '../ExtendedDialog'
import {DeleteIcon, LogoutIcon} from '../icons'
import ProfileInitials from '../settings/ProfileInitials'
import TabPanel from '../TabPanel'
import SandboxSettings from './SandboxSettings'
import ThirdPartyConnections from './ThirdPartyConnections'

const ProfileDialog: FC<ProfileDialogProps> = ({initialTabIndex = 0, onClose, open}) => {
  const styles = useStyles()
  const {merchantUser = {} as MerchantUsers} = useSessionMerchantUser()
  const [tabIndex, setTabIndex] = useState(initialTabIndex)
  const tabPanelLabelAndComponents = useTabPanelLabelAndComponents()
  return (
    <ExtendedDialog
      className={styles.root}
      disableEnforceFocus
      id="profile"
      keepMounted={false}
      maxWidth="md"
      onClose={onClose}
      open={open}
      // To allow inner ClickAwayListener to work.
      onClick={undefined}
    >
      <DialogCloseButton onClick={onClose} />
      <DialogTitle>{useTranslate()('dialogs.profile.title')}</DialogTitle>
      {tabPanelLabelAndComponents.length > 1 && (
        <Tabs
          TabIndicatorProps={{children: <span/>}}
          indicatorColor="primary"
          onChange={(_, newTabIndex: number) => {
            setTabIndex(newTabIndex)
          }}
          textColor="primary"
          value={tabIndex}
          variant="fullWidth"
        >
          {tabPanelLabelAndComponents.map(([label], i) => (
            <Tab key={i} label={label} />
          ))}
        </Tabs>
      )}
      <DialogContent>
        {tabPanelLabelAndComponents.map(([, TabPanelComponent], i) => (
          <TabPanel isSelected={i === tabIndex} key={i} value={tabIndex}>
            <TabPanelComponent merchantUser={merchantUser} />
          </TabPanel>
        ))}
      </DialogContent>
    </ExtendedDialog>
  )
}

const MerchantUserSettings: FC<TabPanelProps> = ({merchantUser}) => {
  const logout = useLogout()
  const translate = useTranslate()
  const styles = useStyles()
  const isDesktop = useIsDesktop()
  if (!merchantUser) return null // <Edit> component raises an error if `id` is unset
  return (
    <>
      <ProfileInitials
        backgroundColor={merchantUser.userColor}
        className={styles.profileInitials}
        name={`${merchantUser.firstName} ${merchantUser.lastName}`}
      />
      <MerchantUserProfileEdit id={merchantUser.id} />
      <ThirdPartyConnections />
      <Divider className={styles.divider} />
      <Toolbar className={styles.toolbar} disableGutters>
        <Grid container>
          {!isDesktop && (
            <Button
              color="primary"
              onClick={logout}
              startIcon={<LogoutIcon />}
              variant="contained"
            >
              {translate('actions.logout')}
            </Button>
          )}
          <UserAccountDeletionButton />
        </Grid>
      </Toolbar>
    </>
  )
}

const UserAccountDeletionButton = () => {
  const {identity: {id} = {}} = useGetIdentity()
  const translate = useTranslate()
  const logout = useLogout()
  const [deleteUserAccount] = useMutation(
    USER_ACCOUNT_DELETION_MUTATION,
    {
      onCompleted: () => logout(),
      onError: e => alert(translate(
        'dialogs.profile.merchantUsersSettings.accountDeletionButtonError',
        {message: e.message}
      )),
      variables: {id},
    }
  )
  return (
    <Button
      color="secondary"
      disabled={!id}
      onClick={() => {
        if (window.confirm(translate(
          'dialogs.profile.merchantUsersSettings.accountDeletionConfirm'
        ))) {
          deleteUserAccount()
        }
      }}
      startIcon={<DeleteIcon color="background" />}
      variant="contained"
    >
      {translate('dialogs.profile.merchantUsersSettings.accountDeletionButton')}
    </Button>
  )
}

const useTabPanelLabelAndComponents = (): ComponentLabelAndComponent<TabPanelProps> => {
  const translate = useTranslate()
  const {isSandbox} = useContext(SandboxContext)
  return useMemo(
    () => [
      [translate('dialogs.profile.merchantUsersSettings.title'), MerchantUserSettings],
      isSandbox && [translate('dialogs.profile.sandbox.title'), SandboxSettings],
    ].filter(Boolean) as ComponentLabelAndComponent<TabPanelProps>,
    [isSandbox, translate],
  )
}


// TODO: In the user we want to be compliant with the way how user information needs to be
//       obfuscated once a user signs off. There should probably be a postgres
//       after-update trigger function or a cron job which does the obfuscation.
const USER_ACCOUNT_DELETION_MUTATION = gql`
  mutation($id: uuid!){
    update_users_by_pk(
      _set: {isDeleted: true}
      pk_columns: {id: $id}
    ){id}
  }
`

const useStyles = makeStyles(theme => ({
  companySettingsAlert: {
    marginBottom: theme.remSpacing(2),
  },
  companySettingsRoot: {
    '& .MuiAccordionDetails-root': {
      display: 'block',
    },
    '& .MuiList-padding': {
      padding: `${theme.remSpacing(.2)} 0`,
    },
    '& .MuiTypography-root': {
      marginLeft: theme.remSpacing(.4),
    },
    '& .MuiTypography-subtitle2': {
      marginBottom: theme.remSpacing(1),
      marginLeft: theme.remSpacing(.4),
      marginTop: theme.remSpacing(2),
    },
  },
  divider: {
    marginBottom: theme.remSpacing(5),
    marginTop: theme.remSpacing(5),
  },
  profileInitials: {
    color: theme.palette.primary.contrastText,
  },
  root: {
    [theme.breakpoints.up('lg')]: {
      '& .MuiDialog-paper': {
        position: 'fixed',
        top: theme.typography.pxToRem(5),
      },
      '& .MuiDialogContent-root': {
        padding: `${theme.remSpacing(0.8)} ${theme.remSpacing(2)}`,
      },
    },
  },
  thirdPartyConnectionsButtons: {
    // TODO: Move this style to the global theme
    '& .MuiButton-root': {
      [theme.breakpoints.down('lg')]: {
        fontSize: theme.typography.button.fontSize,
      },
    },
  },
  thirdPartyConnectionsTitle: {
    padding: `${theme.remSpacing(1)} 0`,
  },
  toolbar: {
    '& .MuiGrid-root': {
      flexDirection: 'row-reverse',
    },
    alignItems: 'flex-end',
    flexDirection: 'column',
    justifyContent: 'end',
  },
}))

interface ProfileDialogProps {
  initialTabIndex?: number,
  onClose: () => void
  open: boolean
}

interface TabPanelProps {
  merchantUser: MerchantUsers | undefined,
}

export default ProfileDialog
